import { ScrollingModule as ExperimentalScrollingModule } from '@angular/cdk-experimental/scrolling'
import { BreakpointObserver } from '@angular/cdk/layout'
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling'
import { NgClass, NgTemplateOutlet } from '@angular/common'
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ContentChildren,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  QueryList,
  signal,
  TrackByFunction,
  ViewChild
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  GenericTableColumnAlign,
  GenericTableFixedFirstColumnMode,
  GenericTableHeaderCheckboxState,
  GenericTableSelectAllCollectionChange,
  GenericTableSelectAllScope,
  GenericTableSelectionChange,
  GenericTableSort,
  GenericTableSortDirection
} from '@app/v2/shared/types/generic-table.types'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { GenericTableColumnDirective } from '../../directives/generic-table-column.directive'
import { buildResponsiveGridTemplateColumns, buildStaticGridTemplateColumns, GenericTableColumnWidthConfig } from '../../utils/generic-table-layout.util'
import { isElementVisible, isNearScrollBottom, isViewportOverflowing } from '../../utils/viewport-scroll.util'
import { TruncationTooltipComponent } from '../truncation-tooltip/truncation-tooltip.component'
import { GENERIC_TABLE_HEADER_HEIGHT_PX, GENERIC_TABLE_ROW_HEIGHT_PX, GENERIC_TABLE_SKELETON_COLORS } from './generic-table.constants'

type GenericTableSkeletonBlock = {
  width: string
  height: string
  borderRadius: string
}

export type GenericTableColumnSkeletonConfig = {
  field: string
  cellPadding: 'default' | 'none'
  align: GenericTableColumnAlign
  layout: 'row' | 'stack'
  layoutClasses: string[]
  blocks: GenericTableSkeletonBlock[]
}

@Component({
  selector: 'app-generic-table',
  standalone: true,
  imports: [ScrollingModule, ExperimentalScrollingModule, NgClass, NgTemplateOutlet, NgxSkeletonLoaderModule, TruncationTooltipComponent],
  templateUrl: './generic-table.component.html',
  styleUrl: './generic-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenericTableComponent<T> implements AfterContentInit {
  private static readonly _SELECT_COLUMN_WIDTH = '48px'

  public readonly items = input.required<T[]>()
  public readonly trackBy = input.required<TrackByFunction<T>>()
  public readonly rowKey = input.required<(row: T) => string>()
  public readonly selectable = input(false)
  public readonly selectedKeys = input<string[]>([])
  public readonly selectAllScope = input<GenericTableSelectAllScope>('firstPage')
  public readonly isAllCollectionSelected = input(false)
  public readonly hasCollectionSelection = input(false)
  public readonly pageSize = input(10)
  public readonly sortDirection = GenericTableSortDirection
  public readonly sort = input<GenericTableSort>({ field: null, direction: GenericTableSortDirection.DESC })
  public readonly hasMore = input(false)
  public readonly isLoadingMore = input(false)
  public readonly isLoading = input(false)
  public readonly stickyHeader = input(true)
  public readonly fixedFirstColumn = input<GenericTableFixedFirstColumnMode>(false)
  public readonly virtualScroll = input(true)
  public readonly virtualScrollMinBufferPx = input(300)
  public readonly virtualScrollMaxBufferPx = input(600)
  public readonly autoFillViewport = input(false)
  public readonly responsiveColumns = input(true)
  public readonly highlightedRowKey = input<string | null>(null)
  public readonly loadingSkeletonRowCount = input(5)
  public readonly rowHeightPx = input(GENERIC_TABLE_ROW_HEIGHT_PX)
  public readonly headerHeightPx = GENERIC_TABLE_HEADER_HEIGHT_PX

  public readonly sortChange = output<GenericTableSort>()
  public readonly selectionChange = output<GenericTableSelectionChange<T>>()
  public readonly selectAllCollectionChange = output<GenericTableSelectAllCollectionChange>()
  public readonly loadMore = output<void>()

  @ContentChildren(GenericTableColumnDirective) private _columnQuery!: QueryList<GenericTableColumnDirective>
  @ViewChild(CdkVirtualScrollViewport) private _viewport?: CdkVirtualScrollViewport

  public readonly headerLabelClasses = 'tw-font-inter tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-500'
  public columns: GenericTableColumnDirective[] = []
  public readonly gridTemplateColumns = signal<string>('')
  public readonly columnSkeletonConfigs = signal<GenericTableColumnSkeletonConfig[]>([])

  public readonly headerCheckboxState = computed((): GenericTableHeaderCheckboxState => {
    const scopeKeys = this._scopeKeys()
    if (!scopeKeys.length) return 'none'

    if (this.selectAllScope() === 'allCollection' && this.isAllCollectionSelected()) return 'allSelected'

    const selected = this._selectedKeySet()
    let selectedInScope = 0

    for (const key of scopeKeys) {
      if (selected.has(key)) selectedInScope++
    }

    if (this.selectAllScope() === 'allCollection' && this.hasCollectionSelection()) return 'someSelected'
    if (selectedInScope === 0) return 'none'
    if (selectedInScope === scopeKeys.length) return 'allSelected'
    return 'someSelected'
  })

  public readonly isFirstColumnFixed = computed(() => {
    const mode = this.fixedFirstColumn()
    if (!mode) return false
    if (mode === true) return true
    if (mode === 'below-xl') return !this._isXlViewport()
    if (mode === 'below-lg') return !this._isLgViewport()

    return false
  })

  public readonly useVirtualScroll = computed(() => this.virtualScroll() && !this.isFirstColumnFixed())

  public readonly loadingSkeletonRows = computed(() => Array.from({ length: this.loadingSkeletonRowCount() }, (_, index) => index))

  public readonly fixedColumnLeftOffset = computed(() => (this.selectable() ? GenericTableComponent._SELECT_COLUMN_WIDTH : '0px'))

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _breakpointObserver = inject(BreakpointObserver)
  private readonly _changeDetectorRef = inject(ChangeDetectorRef)
  private readonly _elementRef = inject(ElementRef<HTMLElement>)
  private readonly _isLgViewport = signal(false)
  private readonly _isXlViewport = signal(false)
  private _containerWidth = 0
  private _columnWidthConfigs: GenericTableColumnWidthConfig[] = []
  private _resizeAnimationFrame: number | null = null
  private _resizeObserver: ResizeObserver | null = null
  private readonly _selectedKeySet = computed(() => new Set(this.selectedKeys()))
  private readonly _scopeKeys = computed(() => {
    const items = this.items()
    const scope = this.selectAllScope()
    const scopeItems = scope === 'firstPage' ? items.slice(0, this.pageSize()) : items
    return scopeItems.map((row) => this.rowKey()(row))
  })
  private _scrollAnimationFrame: number | null = null
  private _viewportSyncFrame: number | null = null
  private _viewportPostSyncFrame: number | null = null
  private _isDestroyed = false

  constructor() {
    effect(() => {
      this.items()
      if (!this._isDestroyed) this._scheduleViewportSync()
    })

    this._destroyRef.onDestroy(() => {
      this._isDestroyed = true

      if (this._scrollAnimationFrame !== null) {
        cancelAnimationFrame(this._scrollAnimationFrame)
      }

      if (this._viewportSyncFrame !== null) {
        cancelAnimationFrame(this._viewportSyncFrame)
      }

      if (this._viewportPostSyncFrame !== null) {
        cancelAnimationFrame(this._viewportPostSyncFrame)
      }

      if (this._resizeAnimationFrame !== null) {
        cancelAnimationFrame(this._resizeAnimationFrame)
      }

      if (this._resizeObserver) {
        this._resizeObserver.disconnect()
      }
    })
  }

  ngAfterContentInit(): void {
    this._syncColumns(this._columnQuery)
    this._columnQuery.changes.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((columns) => this._syncColumns(columns))
    this._observeContainerResize()

    this._breakpointObserver
      .observe(['(min-width: 1024px)', '(min-width: 1280px)'])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((state) => {
        this._isLgViewport.set(state.breakpoints['(min-width: 1024px)'])
        this._isXlViewport.set(state.breakpoints['(min-width: 1280px)'])
        this._updateGridTemplateColumns()
        this._scheduleViewportSync()
        this._changeDetectorRef.markForCheck()
      })
  }

  /**
   * Track rows in the virtual scroll viewport.
   * @param index {number} - The index of the item.
   * @param item {T} - The item.
   * @returns {unknown} - The track by function.
   */
  public trackByFn = (index: number, item: T): unknown => this.trackBy()(index, item)

  /**
   * Resolve a stable row key for selection and tracking.
   * @param row {T} - The row.
   * @returns {string} - The row key.
   */
  public readonly getRowKey = (row: T): string => this.rowKey()(row)

  /**
   * Whether a selection key is currently selected.
   * @param key {string} - The key.
   * @returns {boolean} - Whether the key is selected.
   */
  public isKeySelected(key: string): boolean {
    return this._selectedKeySet().has(key)
  }

  /**
   * Flex justification class for a column alignment.
   * @param align {GenericTableColumnAlign} - Column alignment.
   */
  public columnJustifyClass(align: GenericTableColumnAlign): string {
    switch (align) {
      case 'center':
        return 'tw-justify-center'
      case 'right':
        return 'tw-justify-end'
      default:
        return 'tw-justify-start'
    }
  }

  /**
   * Text alignment class for a column alignment.
   * @param align {GenericTableColumnAlign} - Column alignment.
   */
  public columnTextAlignClass(align: GenericTableColumnAlign): string {
    switch (align) {
      case 'center':
        return 'tw-text-center'
      case 'right':
        return 'tw-text-right'
      default:
        return 'tw-text-left'
    }
  }

  /**
   * Shared body cell classes for data and skeleton rows.
   */
  public bodyCellClasses(column: GenericTableColumnDirective, colIndex: number, index: number, rowFirstColFixed: boolean, isHighlighted = false): string[] {
    const isFixedFirstCol = rowFirstColFixed && colIndex === 0

    return [
      'tw-box-border',
      'tw-flex',
      'tw-items-center',
      'tw-min-w-0',
      'tw-w-full',
      'tw-h-[var(--generic-table-row-height,auto)]',
      'tw-min-h-[var(--generic-table-row-height,auto)]',
      column.cellPadding() === 'none' ? 'tw-py-4' : 'tw-px-4 tw-py-4',
      this.columnJustifyClass(column.align()),
      isFixedFirstCol ? 'tw-sticky' : '',
      isFixedFirstCol ? 'tw-z-[2]' : '',
      isFixedFirstCol ? 'generic-table-fixed-col-edge' : '',
      isFixedFirstCol ? this.rowBackgroundClass(index, isHighlighted) : ''
    ]
  }

  /**
   * Row background class: zebra striping, or the highlight color for the highlighted row.
   * @param index {number} - The row index.
   * @param isHighlighted {boolean} - Whether the row is highlighted.
   * @returns {string} - The background class.
   */
  public rowBackgroundClass(index: number, isHighlighted = false): string {
    if (isHighlighted) return 'tw-bg-blue-50'

    return index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50'
  }

  /**
   * Whether the row matches the highlighted row key.
   * @param row {T} - The row.
   * @returns {boolean} - True when the row is highlighted.
   */
  public isRowHighlighted(row: T): boolean {
    const highlightedKey = this.highlightedRowKey()

    return highlightedKey != null && this.getRowKey(row) === highlightedKey
  }

  /**
   * Shared select-column classes for data and skeleton rows.
   */
  public selectCellClasses(index: number, rowFirstColFixed: boolean, isHighlighted = false): string[] {
    return [
      'tw-box-border',
      'tw-flex',
      'tw-items-center',
      'tw-justify-center',
      'tw-px-3',
      'tw-py-4',
      'tw-h-[var(--generic-table-row-height,auto)]',
      'tw-min-h-[var(--generic-table-row-height,auto)]',
      rowFirstColFixed ? 'tw-sticky' : '',
      rowFirstColFixed ? 'tw-z-[2]' : '',
      rowFirstColFixed ? this.rowBackgroundClass(index, isHighlighted) : ''
    ]
  }

  /**
   * Inner flex classes for skeleton block layout inside a body cell.
   */
  public skeletonInnerCellClasses(skeleton: GenericTableColumnSkeletonConfig): string[] {
    return ['tw-flex', 'tw-min-w-0', 'tw-w-full', ...skeleton.layoutClasses]
  }

  /**
   * Build ngx-skeleton-loader theme for a shimmer cell.
   */
  public skeletonBlockTheme(block: GenericTableSkeletonBlock): Record<string, string> {
    return {
      width: block.width,
      height: block.height,
      'border-radius': block.borderRadius,
      backgroundColor: GENERIC_TABLE_SKELETON_COLORS.backgroundColor,
      color: GENERIC_TABLE_SKELETON_COLORS.color,
      marginBottom: '0'
    }
  }

  /**
   * Toggle selection for a single row.
   */
  public toggleRowSelection(row: T): void {
    const key = this.getRowKey(row)
    const keys = this._selectedKeySet().has(key) ? this.selectedKeys().filter((selectedKey) => selectedKey !== key) : [...this.selectedKeys(), key]

    this._emitSelectionChange(keys)
  }

  /**
   * Toggle selection for all rows in the configured select-all scope.
   * @returns {void} - The void.
   */
  public toggleSelectAll(): void {
    if (this.selectAllScope() === 'allCollection') {
      this.selectAllCollectionChange.emit({ selectAll: this.headerCheckboxState() === 'none' })
      return
    }

    const scopeKeys = this._scopeKeys()
    const scopeSet = new Set(scopeKeys)
    const selected = this._selectedKeySet()
    const allSelected = scopeKeys.length > 0 && scopeKeys.every((key) => selected.has(key))
    const keys = allSelected ? this.selectedKeys().filter((key) => !scopeSet.has(key)) : Array.from(new Set([...this.selectedKeys(), ...scopeKeys]))

    this._emitSelectionChange(keys)
  }

  /**
   * Cycle sort state for a column: desc â†’ asc â†’ clear.
   * @param field {string} - The field.
   * @returns {void} - The void.
   */
  public onColumnHeaderClick(field: string): void {
    const current = this.sort()

    if (current.field === field) {
      if (current.direction === GenericTableSortDirection.DESC) {
        this.sortChange.emit({ field, direction: GenericTableSortDirection.ASC })
        return
      }

      this.sortChange.emit({ field: null, direction: GenericTableSortDirection.DESC })
      return
    }

    this.sortChange.emit({ field, direction: GenericTableSortDirection.DESC })
  }

  /**
   * Load the next page when the virtual scroll viewport nears the bottom.
   * @returns {void} - The void.
   */
  public onViewportScroll(): void {
    const viewport = this._viewport?.elementRef.nativeElement
    if (!viewport || !this._isViewportVisible()) return

    if (this._scrollAnimationFrame !== null) {
      cancelAnimationFrame(this._scrollAnimationFrame)
    }

    this._scrollAnimationFrame = requestAnimationFrame(() => {
      this._scrollAnimationFrame = null

      if (this._isDestroyed || !this._isViewportVisible() || this.isLoading() || this.isLoadingMore() || !this.hasMore()) return

      if (isNearScrollBottom(viewport)) this.loadMore.emit()
    })
  }

  /**
   * Sync the columns.
   * @param columns {QueryList<GenericTableColumnDirective>} - The columns.
   * @returns {void} - The void.
   */
  private _syncColumns(columns: QueryList<GenericTableColumnDirective>): void {
    this.columns = columns.toArray()
    this._columnWidthConfigs = this.columns.map((column) => ({
      width: column.width(),
      widthXl: column.widthXl(),
      minWidth: column.minWidth(),
      maxWidth: column.maxWidth(),
      columnSize: column.columnSize()
    }))
    this.columnSkeletonConfigs.set(this.columns.map((column) => this._buildColumnSkeletonConfig(column)))
    this._updateGridTemplateColumns()
  }

  /**
   * Rebuild grid template columns from the current container width and breakpoint.
   * @returns {void}
   */
  private _updateGridTemplateColumns(): void {
    this.gridTemplateColumns.set(this._buildGridTemplateColumns())
  }

  /**
   * Build the grid template columns.
   * @returns {string} - The grid template columns.
   */
  private _buildGridTemplateColumns(): string {
    if (this.responsiveColumns() && this._containerWidth > 0) {
      const scaledGridTrackSizes = buildResponsiveGridTemplateColumns(this._columnWidthConfigs, this._containerWidth, this.selectable(), this._isXlViewport())

      if (scaledGridTrackSizes) return scaledGridTrackSizes
    }

    return buildStaticGridTemplateColumns(this._columnWidthConfigs, this.selectable(), this._isXlViewport())
  }

  /**
   * Observe table width changes and rescale columns proportionally.
   * @returns {void}
   */
  private _observeContainerResize(): void {
    if (!this.responsiveColumns() || typeof ResizeObserver === 'undefined') {
      this._containerWidth = this._elementRef.nativeElement.clientWidth
      this._updateGridTemplateColumns()
      return
    }

    this._containerWidth = this._elementRef.nativeElement.clientWidth
    this._updateGridTemplateColumns()

    this._resizeObserver = new ResizeObserver(() => {
      if (this._resizeAnimationFrame !== null) {
        cancelAnimationFrame(this._resizeAnimationFrame)
      }

      this._resizeAnimationFrame = requestAnimationFrame(() => {
        this._resizeAnimationFrame = null

        if (this._isDestroyed) return

        const width = this._elementRef.nativeElement.clientWidth
        if (width === this._containerWidth) return

        this._containerWidth = width
        this._updateGridTemplateColumns()
        this._viewport?.checkViewportSize()
        this._changeDetectorRef.markForCheck()
      })
    })

    this._resizeObserver.observe(this._elementRef.nativeElement)
  }

  /**
   * Emit the selection change.
   * @param keys {string[]} - The keys.
   * @returns {void} - The void.
   */
  private _emitSelectionChange(keys: string[]): void {
    const keySet = new Set(keys)
    const rows = this.items().filter((row) => keySet.has(this.getRowKey(row)))
    this.selectionChange.emit({ keys, rows })
  }

  /**
   * Whether the viewport is visible.
   * @returns {boolean} - Whether the viewport is visible.
   */
  private _isViewportVisible(): boolean {
    return isElementVisible(this._viewport?.elementRef.nativeElement)
  }

  /**
   * Schedule the viewport sync.
   * @returns {void} - The void.
   */
  private _scheduleViewportSync(): void {
    if (this._isDestroyed || this._viewportSyncFrame !== null) return

    if (this._viewportPostSyncFrame !== null) {
      cancelAnimationFrame(this._viewportPostSyncFrame)
      this._viewportPostSyncFrame = null
    }

    this._viewportSyncFrame = requestAnimationFrame(() => {
      this._viewportSyncFrame = null

      if (this._isDestroyed || !this._isViewportVisible()) return

      this._viewport?.checkViewportSize()

      this._viewportPostSyncFrame = requestAnimationFrame(() => {
        this._viewportPostSyncFrame = null

        if (this._isDestroyed || !this._isViewportVisible()) return

        this._viewport?.checkViewportSize()
        this._loadMoreIfViewportNotFilled()
      })
    })
  }

  /**
   * Load more if the viewport is not filled.
   * @returns {void} - The void.
   */
  private _loadMoreIfViewportNotFilled(): void {
    if (!this.autoFillViewport()) return
    if (this._isDestroyed || !this._isViewportVisible() || this.isLoading() || this.isLoadingMore() || !this.hasMore()) return

    if (this.items().length >= this.pageSize()) return

    const element = this._viewport?.elementRef.nativeElement as HTMLElement | undefined
    if (!element) return

    if (!isViewportOverflowing(element)) this.loadMore.emit()
  }

  /**
   * Build precomputed skeleton config for a column.
   */
  private _buildColumnSkeletonConfig(column: GenericTableColumnDirective): GenericTableColumnSkeletonConfig {
    const blockCount = this._resolveSkeletonBlockCount(column)
    const layout = column.skeletonLayout()
    const align = column.align()
    const useRowLayout = layout === 'row' && blockCount > 1

    return {
      field: column.field(),
      cellPadding: column.cellPadding(),
      align,
      layout: useRowLayout ? 'row' : 'stack',
      layoutClasses: this._skeletonLayoutClasses(useRowLayout, align),
      blocks: this._buildSkeletonBlocks(blockCount, useRowLayout, align)
    }
  }

  private _resolveSkeletonBlockCount(column: GenericTableColumnDirective): number {
    const configured = column.skeletonBlockCount()
    if (configured != null) return Math.max(1, configured)

    if (column.align() === 'center' || column.align() === 'right') return 1

    return 2
  }

  private _skeletonLayoutClasses(useRowLayout: boolean, align: GenericTableColumnAlign): string[] {
    if (useRowLayout) {
      return ['tw-flex-row', 'tw-gap-2', align === 'center' ? 'tw-justify-center' : 'tw-justify-start']
    }

    return ['tw-flex-col', 'tw-gap-1.5', align === 'center' ? 'tw-items-center' : 'tw-items-start']
  }

  private _buildSkeletonBlocks(blockCount: number, useRowLayout: boolean, align: GenericTableColumnAlign): GenericTableSkeletonBlock[] {
    if (useRowLayout) {
      return Array.from({ length: blockCount }, () => ({ width: '28px', height: '28px', borderRadius: '9999px' }))
    }

    if (blockCount === 1) {
      return [{ width: this._skeletonBarWidth(align), height: '14px', borderRadius: '4px' }]
    }

    const stackedWidths = ['72%', '52%']
    return Array.from({ length: blockCount }, (_, index) => ({
      width: stackedWidths[index] ?? '40%',
      height: index === 0 ? '14px' : '12px',
      borderRadius: '4px'
    }))
  }

  private _skeletonBarWidth(align: GenericTableColumnAlign): string {
    switch (align) {
      case 'center':
        return '48px'
      case 'right':
        return '56px'
      default:
        return '70%'
    }
  }
}
